import React, { ReactElement, useMemo, useState, useCallback } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useDeferredValueCompat } from '../../../hooks/useDeferredValueCompat'
import ReactMarkdown from 'react-markdown'
import { getComponent } from './ComponentRegistry'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { type ToolResultDetail } from '@/core/models/medconnect-agent.model'
import remarkGfm from 'remark-gfm'
import { useTheme } from '@mui/material/styles'
import { normalizeUtf8 } from '@/utils/encoding'

/**
 * Code block wrapper with a copy button in the header bar.
 */
function CodeBlockWithCopy({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
    const [copied, setCopied] = useState(false)

    // Extract the text content and language from the <code> child
    let language = ''
    const codeChild = React.Children.toArray(children).find(
        (child) => React.isValidElement(child) && (child as React.ReactElement<any>).type === 'code'
    ) as React.ReactElement<any> | undefined

    if (codeChild?.props?.className) {
        const match = (codeChild.props.className as string).match(/language-(\S+)/)
        if (match) language = match[1]
    }

    const getTextContent = useCallback((node: React.ReactNode): string => {
        if (typeof node === 'string') return node
        if (typeof node === 'number') return String(node)
        if (!React.isValidElement(node)) return ''
        const el = node as React.ReactElement<any>
        return React.Children.toArray(el.props.children).map(getTextContent).join('')
    }, [])

    const handleCopy = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation()
        try {
            const text = codeChild ? getTextContent(codeChild) : ''
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Fallback for older browsers
            const text = codeChild ? getTextContent(codeChild) : ''
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }, [codeChild, getTextContent])

    return (
        <Box sx={{ position: 'relative', my: 1 }}>
            {/* Header bar with language label and copy button */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                    borderTopLeftRadius: (theme) => theme.shape.borderRadius,
                    borderTopRightRadius: (theme) => theme.shape.borderRadius,
                    px: 1.5,
                    py: 0.5,
                    minHeight: 32,
                }}
            >
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, textTransform: 'uppercase', opacity: 0.7 }}>
                    {language || 'code'}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                    <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                        {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Tooltip>
            </Box>
            {/* The actual <pre> block, with top border radius removed */}
            <pre
                {...props}
                style={{
                    margin: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                }}
            >
                {children}
            </pre>
        </Box>
    )
}

/**
 * Compact tool call display — Cloudflare-style "→ toolName" inline.
 */
const CompactToolCallGroup: React.FC<{ items: ToolResultDetail[] }> = ({ items }) => {
    const [expanded, setExpanded] = useState(false)
    const hasError = items.some((it) => {
        try {
            const r = typeof it.result === 'string' ? JSON.parse(it.result) : it.result
            return r?.error
        } catch { return false }
    })

    const humanize = (name: string) => name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')

    return (
        <Box sx={{ my: 0.5 }}>
            <Box
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    '&:hover': { color: 'text.primary' },
                }}
            >
                <Typography component="span" sx={{ fontSize: '0.7rem', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                    ▶
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {items.map((it) => humanize(it.tool_name || 'tool')).join(', ')}
                </Typography>
                {hasError && <Typography component="span" sx={{ fontSize: '0.75rem', color: 'error.main' }}>failed</Typography>}
            </Box>
            {expanded && (
                <Box sx={{ pl: 2, mt: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                    {items.map((it, idx) => (
                        <Box key={it.tool_call_id || idx} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                → {humanize(it.tool_name || 'tool')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

const CompactToolCallDisplay: React.FC<{ toolInvocation: ToolResultDetail }> = ({ toolInvocation }) => {
    const humanize = (name: string) => name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
    return (
        <Box sx={{ my: 0.25 }}>
            <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                → {humanize(toolInvocation.tool_name || 'tool')}
            </Typography>
        </Box>
    )
}

interface MarkdownProcessorProps {
    content: string
    /** Use minimal inline tool call rendering */
    compact?: boolean
}

export const MarkdownProcessor: React.FC<MarkdownProcessorProps> = ({ content, compact = false }) => {
    const theme = useTheme()
    const deferredContent = useDeferredValueCompat(normalizeUtf8(content))
    const remarkPlugins = useMemo(() => [remarkGfm], [])

    const markdownComponents = useMemo(() => ({
        pre: CodeBlockWithCopy,
        a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
            if (!href) return <a {...props}>{children}</a>

            // Extract path from full URLs pointing to the same origin
            let path = href
            try {
                const url = new URL(href, window.location.origin)
                if (url.origin === window.location.origin) {
                    path = url.pathname + url.search + url.hash
                }
            } catch {
                // not a valid URL, use as-is
            }

            // Internal paths → React Router Link (no reload, SPA navigation)
            if (path.startsWith('/')) {
                return <RouterLink to={path}>{children}</RouterLink>
            }

            // External links → normal <a> in new tab
            return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
        },
    }), [])

    const processedElements = useMemo(() => {
        if (!deferredContent) return [] as ReactElement[]

        const parts: (string | ReactElement)[] = []
        const componentRegex = /\[([\w-]+)\]\(([^)]*)\)/g
        let lastIndex = 0
        let match
        let pendingGroup: ToolResultDetail[] = []
        let groupCounter = 0

        const flushGroupIfAny = () => {
            if (pendingGroup.length === 0) return
            if (compact) {
                const groupKey = `tool-group-compact-${groupCounter++}-${lastIndex}`
                parts.push(React.createElement(CompactToolCallGroup, { items: pendingGroup, key: groupKey }))
            } else {
                const GroupComponent = getComponent('tool_call_group')
                if (GroupComponent) {
                    const groupKey = `tool-group-${groupCounter++}-${lastIndex}`
                    parts.push(React.createElement(GroupComponent, { items: pendingGroup, key: groupKey }))
                }
            }
            pendingGroup = []
        }

        while ((match = componentRegex.exec(deferredContent)) !== null) {
            if (match.index > lastIndex) {
                const interstitial = deferredContent.substring(lastIndex, match.index)
                const isWhitespaceOnly = interstitial.trim().length === 0
                if (!isWhitespaceOnly) {
                    // Before pushing substantive text, place any pending tool group
                    flushGroupIfAny()
                    parts.push(interstitial)
                }
                // If it's just whitespace/newlines, we skip adding it and keep grouping contiguous tool calls
            }

            const [fullMatch, componentName, rawPayload] = match

            const ComponentToRender = getComponent(componentName)

            // Skip non-component matches — let ReactMarkdown handle them as normal links
            if (!ComponentToRender) {
                continue
            }

            if (ComponentToRender) {
                try {
                    let props: Record<string, any> = {}
                    if (componentName === 'tool_interaction') {
                        const jsonPayload = atob(rawPayload)
                        const parsedProps = JSON.parse(jsonPayload) as ToolResultDetail
                        props = { toolInvocation: parsedProps }

                        // Group non-smart tool calls; leave smart LLM visible
                        const toolName = parsedProps.tool_name || ''
                        const lowered = toolName.toLowerCase()
                        const isSmart = toolName === 'smartLLMAsk' || lowered.includes('smart') || lowered.includes('llm')

                        if (!isSmart) {
                            pendingGroup.push(parsedProps)
                            // Do not push individual component; continue to next
                            lastIndex = componentRegex.lastIndex
                            continue
                        } else {
                            // Flush any existing group before rendering smart tool
                            flushGroupIfAny()
                        }

                        // In compact mode, render smart tools inline too
                        if (compact) {
                            const elementKey = `${componentName}-compact-${match.index}`
                            parts.push(React.createElement(CompactToolCallDisplay, { toolInvocation: parsedProps, key: elementKey }))
                            lastIndex = componentRegex.lastIndex
                            continue
                        }
                    } else {
                        props = rawPayload ? JSON.parse(rawPayload) : {}
                    }

                    const elementKey = `${componentName}-${match.index}`
                    parts.push(React.createElement(ComponentToRender, { ...props, key: elementKey }))
                } catch (error) {
                    if (process.env.NODE_ENV !== 'production') {
                        // eslint-disable-next-line no-console
                        console.error(`[MarkdownProcessor] Error processing component <${componentName}>:`, error)
                    }
                    parts.push(fullMatch)
                }
            }

            lastIndex = componentRegex.lastIndex
        }

        // Flush any remaining grouped tools before trailing text
        flushGroupIfAny()

        if (lastIndex < deferredContent.length) {
            parts.push(deferredContent.substring(lastIndex))
        }

        const finalElements: ReactElement[] = []
        let accumulatedText = ''

        parts.forEach((part, index) => {
            if (typeof part === 'string') {
                accumulatedText += part
            } else {
                if (accumulatedText) {
                    finalElements.push(
                        <ReactMarkdown key={`md-text-${index}`} remarkPlugins={remarkPlugins} components={markdownComponents}>
                            {accumulatedText}
                        </ReactMarkdown>
                    )
                    accumulatedText = ''
                }
                finalElements.push(part)
            }
        })

        if (accumulatedText) {
            finalElements.push(
                <ReactMarkdown key="md-text-last" remarkPlugins={remarkPlugins} components={markdownComponents}>
                    {accumulatedText}
                </ReactMarkdown>
            )
        }

        return finalElements
    }, [deferredContent, remarkPlugins, markdownComponents, compact])

    // Compact mode: tighter spacing, smaller font, no pre-wrap (kills extra whitespace)
    const baseSx = compact ? {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(0.25),
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const,
        whiteSpace: 'normal' as const,
        px: 0,
        color: theme.palette.text.primary,
        fontSize: '0.84rem',
        lineHeight: 1.55,
    } : {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(1),
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const,
        whiteSpace: 'pre-wrap' as const,
        px: 1,
        color: theme.palette.text.primary,
        fontSize: '0.95rem',
        lineHeight: 1.7,
    }

    return (
        <Box sx={{
            ...baseSx,

            '& > :last-child': {
                marginBottom: 0
            },

            '& p': {
                margin: 0,
            },
            '& ul, & ol, & pre, & blockquote, & table': {
                margin: 0,
            },

            '& p:empty': {
                display: 'none',
            },

            '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                    textDecoration: 'underline'
                }
            },

            '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: compact ? theme.spacing(1) : theme.spacing(2),
                marginBottom: compact ? theme.spacing(0.5) : theme.spacing(1),
                fontWeight: 600,
                lineHeight: 1.3,
                borderBottom: compact ? 'none' : `1px solid ${theme.palette.divider}`,
                paddingBottom: compact ? 0 : theme.spacing(0.5),
                fontSize: compact ? '0.9rem' : undefined,
            },
            '& h2': {
                marginTop: compact ? theme.spacing(0.75) : theme.spacing(1.5),
                marginBottom: compact ? theme.spacing(0.5) : theme.spacing(1),
                fontWeight: 700,
                lineHeight: 1.3,
                borderBottom: compact ? 'none' : `1px solid ${theme.palette.divider}`,
                paddingBottom: compact ? 0 : theme.spacing(0.25),
            },
            '& strong, & b': {
                fontWeight: 700,
            },

            '& ul, & ol': {
                paddingLeft: compact ? theme.spacing(2) : theme.spacing(3),
                listStyle: 'initial',
                marginTop: compact ? theme.spacing(0.25) : theme.spacing(1),
                marginBottom: compact ? theme.spacing(0.25) : theme.spacing(1),
            },
            '& li': {
                marginBottom: compact ? theme.spacing(0.125) : theme.spacing(0.5),
            },
            '& li p': {
                margin: 0,
            },
            // Code blocks
            '& pre': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                padding: compact ? theme.spacing(1) : theme.spacing(2),
                borderRadius: theme.shape.borderRadius,
                overflowX: 'auto',
                fontSize: compact ? '0.8rem' : '0.9rem',
            },
            '& code': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                padding: '0.15em 0.3em',
                borderRadius: '3px',
                fontSize: compact ? '0.78rem' : '0.875rem',
                fontFamily: 'monospace'
            },
            '& pre > code': { // code inside pre should not have the inline styling
                padding: 0,
                backgroundColor: 'transparent',
                fontSize: 'inherit',
                fontFamily: 'inherit'
            },

            // Blockquotes
            '& blockquote': {
                borderLeft: `4px solid ${theme.palette.divider}`,
                paddingLeft: theme.spacing(2),
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                '& p': {
                    margin: 0
                }
            },

            // Tables
            '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                overflow: 'hidden',
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.divider}`
            },
            '& th, & td': {
                padding: theme.spacing(1.5),
                borderBottom: `1px solid ${theme.palette.divider}`,
                textAlign: 'left'
            },
            '& thead tr': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                fontWeight: 600
            },
            '& tbody tr:last-of-type th, & tbody tr:last-of-type td': {
                borderBottom: 'none'
            },
            '& tbody tr:nth-of-type(even)': {
                backgroundColor: theme.palette.action.hover
            }
        }}>
            {processedElements}
        </Box >
    )
}

export default React.memo(MarkdownProcessor)